import { StatsigClient } from '@statsig/js-client'
// Not part of @statsig/js-client's public entry (only StatsigClient/
// StatsigOptions are re-exported there) — this is Statsig's own documented
// bootstrap pattern regardless: https://docs.statsig.com/client/javascript-sdk/using-evaluations-data-adapter
// names the exact class this deep import reaches, it's just shipped as a
// subpath rather than a root export.
import { StatsigEvaluationsDataAdapter } from '@statsig/js-client/src/StatsigEvaluationsDataAdapter'

import { STABLE_ID_COOKIE } from '@/constants'
import { env, isOnServer } from '@/utils'
import { getCookieClient } from '@/utils/client'

import type { IStatsigBootstrapUser } from './StatsigBootstrapScript'
import { resolveStatsigTier } from './statsigEnvironmentTier'

let client: StatsigClient | null = null

const BOOTSTRAP_ELEMENT_ID = '__STATSIG_BOOTSTRAP__'

interface IStatsigBootstrapData {
	payload: string
	stableId: string
	user?: IStatsigBootstrapUser
}

/**
 * The user the server evaluated the payload against, when the page embedded
 * it. Falling back to the stable id alone keeps a page rendered before that
 * field existed working — it just costs the attribute alignment, exactly as
 * before.
 */
const bootstrapUser = (
	bootstrap: IStatsigBootstrapData,
): IStatsigBootstrapUser =>
	bootstrap.user ?? { customIDs: { stableID: bootstrap.stableId } }

const readBootstrapFromDom = (): IStatsigBootstrapData | null => {
	const el = document.getElementById(BOOTSTRAP_ELEMENT_ID)
	if (!el?.textContent) return null

	try {
		const parsed = JSON.parse(
			el.textContent,
		) as Partial<IStatsigBootstrapData>
		if (!parsed.payload || !parsed.stableId) return null
		return parsed as IStatsigBootstrapData
	} catch (error) {
		console.error('[statsig] failed to parse bootstrap payload', error)
		return null
	}
}

/**
 * MYQRCODE-10818 — the single call site allowed to construct `client`. On
 * builder/checkout, the page itself has rendered a `<script id="__STATSIG_
 * BOOTSTRAP__">` tag (see QrDesignFormPage.tsx / CheckoutPage.tsx) carrying
 * the server-evaluated payload + the same stable id the server used; this
 * reads it synchronously and hydrates the client with zero network fetch —
 * no flicker (AC#1/#2). Every other route has no such tag, so this falls
 * through to MYQRCODE-10817's original bare parity-test path unchanged.
 *
 * This MUST stay the only place `client` is assigned — `UseStatsig` mounts
 * in RootProviders.tsx, an ancestor of every page including builder/
 * checkout themselves, so its mount effect always completes before any
 * effect nested inside the page's own tree. A second, page-local init
 * attempt would always lose that race silently (the nullness guard below
 * would already be false), never bootstrap, and produce no error — hence
 * reading from the DOM here instead of accepting bootstrap data as a prop
 * on a second component.
 */
export const initStatsigOnce = () => {
	// `client` is only ever assigned synchronously below, so its nullness
	// alone is enough to guard against re-initialising — no separate flag
	// needed (unlike posthog.ts, whose imported singleton is always
	// truthy even before init, and so can't signal "initialized" this way).
	if (isOnServer || client || !env.STATSIG_CLIENT_KEY) return

	// AC#3 — an unrecognised/absent tier disables Statsig, same as the server
	// SDK in statsig.server.ts.
	const tier = resolveStatsigTier(env.STATSIG_TIER)
	if (!tier) return

	const bootstrap = readBootstrapFromDom()

	if (bootstrap) {
		try {
			// The adapter only becomes "attached" once passed into the
			// StatsigClient constructor below (StatsigClientBase's own
			// constructor is what calls dataAdapter.attach() internally) —
			// calling setData() before that point throws "EvaluationsDataAdapter
			// is not attached to a Client". Construct the client first, THEN
			// seed it, THEN initialize.
			const dataAdapter = new StatsigEvaluationsDataAdapter()

			client = new StatsigClient(
				env.STATSIG_CLIENT_KEY,
				// Must match the user the server embedded in `bootstrap.payload`
				// field for field, or the client fails to find the bootstrapped
				// cache entry (losing the "no flicker" guarantee) and Statsig
				// flags every exposure as a partial user match.
				bootstrapUser(bootstrap),
				{ environment: { tier }, dataAdapter },
			)

			dataAdapter.setData(bootstrap.payload)

			const details = client.initializeSync()
			if (!details.success) {
				console.error(
					'[statsig] client SDK failed to initialise from bootstrap',
					details,
				)
			}
			lastAppliedBootstrapPayload = bootstrap.payload
			return
		} catch (error) {
			// Fall through to the bare path below — fail-open (AC#8). Reset
			// `client` so the bare path's guard doesn't see a half-constructed
			// instance and skip itself.
			console.error('[statsig] bootstrap hydration failed', error)
			client = null
		}
	}

	const stableId = getCookieClient(STABLE_ID_COOKIE)

	client = new StatsigClient(
		env.STATSIG_CLIENT_KEY,
		stableId ? { customIDs: { stableID: stableId } } : {},
		{ environment: { tier } },
	)

	// initializeAsync() resolves with { success: false } rather than
	// rejecting for realistic failures (bad key, network down) — a bare
	// `void` here would swallow that with zero visibility, so the resolved
	// value is checked explicitly. The .catch() covers anything that
	// genuinely does reject.
	client
		.initializeAsync()
		.then((details) => {
			if (!details.success) {
				console.error(
					'[statsig] client SDK failed to initialise',
					details.error,
				)
				return
			}

			console.log(
				`[statsig] client SDK initialised successfully (tier: ${tier})`,
			)
		})
		.catch((error: unknown) => {
			console.error('[statsig] client SDK failed to initialise', error)
		})
}

export const getStatsigClient = () => client

let lastAppliedBootstrapPayload: string | null = null

/**
 * MYQRCODE-10820 — re-syncs an already-constructed client with the CURRENT
 * page's bootstrap script tag. Needed because `initStatsigOnce` runs exactly
 * once per full page load (UseStatsig mounts in the shared layout, which
 * never remounts on client-side navigation) — so a visitor who lands on the
 * builder and CLICKS through to checkout keeps the client identity from
 * whichever page loaded first, and checkout's own bootstrap tag is never
 * read. On a first-ever visit that first identity can even be the bare
 * anonymous fallback (no stableID at all), which is exactly the
 * "does not have the required id_type stableID" warning seen live on UAT.
 *
 * Called from useStatsigExposure (which, unlike UseStatsig, mounts per page
 * and so runs again on each client-side navigation). Idempotent: skips if
 * the current tag's payload was already applied, so StrictMode double
 * effects and repeat visits to the same page are no-ops.
 */
export const syncStatsigFromDomBootstrap = (): void => {
	if (isOnServer || !client) return

	const bootstrap = readBootstrapFromDom()
	if (!bootstrap || bootstrap.payload === lastAppliedBootstrapPayload) return

	try {
		client.dataAdapter.setData(bootstrap.payload)
		client.updateUserSync(bootstrapUser(bootstrap))
		lastAppliedBootstrapPayload = bootstrap.payload
	} catch (error) {
		// Fail open (AC#8): the client keeps whatever identity/values it had.
		console.error('[statsig] bootstrap re-sync failed', error)
	}
}
